import { Injectable } from '@angular/core';
import { ApiService } from '@api/api.service';
import { AccountSettingsKeys, UserSettingsKeys } from '@shared/types/settings-keys';
import { Subject } from 'rxjs';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class PreferencesService {
  documentNotificationChangedAnnounceSource = new Subject<boolean>();
  documentNotificationChangedAnnounce$ = this.documentNotificationChangedAnnounceSource.asObservable();

  tableAutoRefreshOnImportChangedAnnounceSource = new Subject<boolean>();
  tableAutoRefreshOnImportChangedAnnounce$ = this.tableAutoRefreshOnImportChangedAnnounceSource.asObservable();

  historyVisibilityChangedAnnounceSource = new Subject<boolean>();
  historyVisibilityChangedAnnounce$ = this.historyVisibilityChangedAnnounceSource.asObservable();

  constructor(private localStorage: LocalStorageService, private api: ApiService) {}

  /**
   * Fetch setting from local storage,
   * If not exist then fetch from API. Default is true.
   *
   * @param type the setting type ('User' or 'Account')
   * @param setting the setting's name
   */
  async getSetting(
    type: 'User' | 'Account',
    setting: UserSettingsKeys | AccountSettingsKeys,
    defaultValue: any = true,
    forceFromApi: boolean = false
  ) {
    try {
      // First we try to get the value from local storage, unless forceFromApi === true
      const settingFromLocal = this.localStorage[setting];
      if (settingFromLocal !== null && settingFromLocal !== undefined && !forceFromApi) {
        return settingFromLocal;
      } else {
        // If the value was not in the storage or forceFromApi === true, we try to obtain it from the API
        let allSettingsFromAPI: Promise<any>;
        switch (type) {
          case 'User':
            allSettingsFromAPI = await this.api.users.byId.settings.get(this.localStorage.userWithToken.id).toPromise();
            break;
          case 'Account':
            allSettingsFromAPI = await this.api.accounts.byId.settings
              .get(this.localStorage.userWithToken.account_id)
              .toPromise();
            break;
        }
        // If the value was in the API, we save it in the storage
        if (allSettingsFromAPI && allSettingsFromAPI[setting] !== null && allSettingsFromAPI[setting] !== undefined) {
          this.localStorage[setting] = allSettingsFromAPI[setting];
          return allSettingsFromAPI[setting];
        } else {
          // If the value was not found, we assign it the default value and it is saved in the storage and in the API
          this.localStorage[setting] = defaultValue;
          switch (type) {
            case 'User':
              await this.api.users.byId.settings
                .post(this.localStorage.userWithToken.id, setting as UserSettingsKeys, defaultValue)
                .toPromise();
              break;
            case 'Account':
              await this.api.accounts.byId.settings
                .patch(this.localStorage.userWithToken.account_id, setting as AccountSettingsKeys, defaultValue)
                .toPromise();
              break;
          }
          return defaultValue;
        }
      }
    } catch (error) {
      alert('An error occurred, please restart your browser and log in again.');
      throw error;
    }
  }

  /**
   * Globally set the user preference setting to local storage also sync to API
   *
   * @param value Setting value
   * @param setting Setting name
   */
  setUserPreferenceSetting(setting: boolean, type: UserSettingsKeys): void {
    this.localStorage[type] = setting;

    this.api.users.byId.settings
      .post(this.localStorage.userWithToken.id, type, setting)
      .toPromise()
      .then(() => {});

    switch (type) {
      case UserSettingsKeys.documentNotifications:
        this.documentNotificationChangedAnnounceSource.next(setting);
        break;
      case UserSettingsKeys.tableAutoRefreshOnImport:
        this.tableAutoRefreshOnImportChangedAnnounceSource.next(setting);
        break;
    }
  }

  /**
   * Globally set the account preference setting to local storage also sync to API
   *
   * @param value Setting value
   * @param setting Setting name
   */
  setAccountPreferenceSetting(value: boolean, setting: AccountSettingsKeys): void {
    this.localStorage[setting] = value;

    this.api.accounts.byId.settings
      .patch(this.localStorage.userWithToken.account_id, setting, value)
      .toPromise()
      .then(() => {});
  }
}
